import { AnimatePresence, motion } from 'framer-motion'
import React, { Suspense, useEffect, useRef, useState } from 'react'
import { useAuth } from '../../context/auth/useAuth'

import { ChevronDown } from 'lucide-react'
import { getInitials } from '../../utils/getInitials'
import { UserMenu } from './UserMenu'

export const UserButton: React.FC = () => {
  const { user } = useAuth()

  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  if (!user) {
    return null
  }

  const displayName = user.displayName || user.email?.split('@')[0] || 'User'

  const getFirstName = (name: string) => {
    return name.split(' ')[0]
  }

  return (
    <div className="absolute flex gap-2 z-[200] top-4 right-4" ref={menuRef}>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setIsOpen(!isOpen)
        }}
        className="cursor-pointer flex items-center space-x-2 sm:space-x-3 bg-interactive-secondary text-text-primary hover:bg-interactive-secondary-hover rounded-xl px-2 sm:px-4 py-2 sm:py-3 transition-all duration-200 focus:ring-2 focus:ring-primary-500/50 focus:border-primary-shell-primary-500/30 shadow-sm backdrop-blur-sm h-10 sm:h-12"
      >
        <div className="relative w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-500 rounded-xl flex items-center justify-center text-white text-xs sm:text-sm font-semibold shadow-sm">
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt={displayName}
              className="w-full h-full rounded-xl object-cover"
              width={32}
              height={32}
            />
          ) : (
            <span className="text-xs sm:text-sm">{getInitials(displayName)}</span>
          )}
        </div>

        <span className="text-xs sm:text-sm font-medium text-text-primary hidden md:block max-w-16 lg:max-w-24 truncate">
          {getFirstName(displayName)}
        </span>

        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <Suspense fallback={<div />}>
            <UserMenu user={user} />
          </Suspense>
        )}
      </AnimatePresence>
    </div>
  )
}
